<template>
    <div class="p-4">
        <b-alert show :variant="isWaitingMode ? 'info' : 'danger'" class="d-flex align-items-center mb-4">
            <i class="ri-information-line mr-3" style="font-size: 1.5rem;" v-if="isWaitingMode"></i>
            <i class="ri-error-warning-line mr-3" style="font-size: 1.5rem;" v-else></i>
            <div>
                <strong>{{ isWaitingMode ? ($t('status_waiting') || 'Beklemede') : ($t('waiters_rejected_courses') || 'Ders Seçimi Reddedildi') }}</strong><br>
                {{ isWaitingMode ? ($t('waiting_secretary_approval_desc') || 'Fakülte Sekreteri Onayı Bekleniyor') : ($t('please_reupload_rejected_courses') || 'Lütfen reddedilen derslerinizi listeden çıkarıp yerine yeni dersler seçerek kaydediniz.') }}
            </div>
        </b-alert>

        <fieldset class="border p-3 mb-4 rounded bg-white shadow-sm position-relative" v-if="!isWaitingMode">
            <b-col class="d-flex justify-content-center align-items-center flex-column">
                <b-col cols="12" md="8" lg="6" class="mb-2 mb-md-0">
                    <b-form-group :label="$t('courses')">
                        <multiselect
                            :multiple="false"
                            v-model="pendingSelectedCourseVal"
                            :options="sections"
                            label="text"
                            track-by="value"
                            :placeholder="$t('courses')"
                            :searchable="true"
                            @select="onCourseSelected"
                        >
                        </multiselect>
                    </b-form-group>
                </b-col>
                <b-col cols="12" md="8" lg="6" class="d-flex justify-content-center">
                    <b-button variant="outline-dark" @click="addCourse" size="sm" class="px-4">
                        {{ $t('add')}}
                    </b-button>
                </b-col>
            </b-col>

            <div v-if="['YL','D'].includes(formData.target_program_level)" class="position-absolute text-muted small font-weight-bold" style="top: 15px; right: 20px;">
                *{{ $t('graduate_course_limit_warning') }}
            </div>
        </fieldset>

        <div class="mb-4">
             <div class="font-weight-bold mb-2">{{ $t('selected_courses') }}</div>
             <b-table bordered responsive small hover head-variant="light" :items="selectedCourses" :fields="courseFields" class="font-size-13 text-center bg-white shadow-sm">
                 <template #cell(checkbox)="data">
                     <b-form-checkbox v-model="data.item.is_student_selected" :disabled="isWaitingMode"></b-form-checkbox>
                 </template>
                 <template #cell(course_day)="data">
                     <div v-html="dayCalculate(data.item?.schedules)"></div>
                 </template>
                 <template #cell(status)="data">
                     <b-badge :variant="['approved'].includes(data.item.status)? 'success' : (['rejected'].includes(data.item.status) ? 'danger' : 'secondary')">
                         {{ ['approved'].includes(data.item.status)? $t('approved') : (['rejected'].includes(data.item.status) ? $t('rejected') : $t('status_waiting')) }}
                     </b-badge>
                 </template>
                 <template #cell(actions)="data">
                     <b-button size="sm" variant="outline-danger" @click="removeCourse(data.item)" v-if="!['approved'].includes(data.item.status) && !isWaitingMode" class="font-weight-bold px-3 py-0"><i class="ri-delete-bin-line"></i></b-button>
                 </template>
             </b-table>
        </div>

        <div class="mb-4 font-size-12 text-muted font-weight-bold">
            <i class="ri-information-line"><div v-html="$t('fee_disclaimer_message')"></div></i>
        </div>

        <div class="d-flex justify-content-center mt-3" v-if="!isWaitingMode">
            <b-button variant="primary" @click="submitAndNext" class="px-5 font-weight-bold shadow-sm">
                <i class="ri-save-line mr-1"></i> {{ $t('save')}}
            </b-button>
        </div>

        <b-modal ref="warningModal" hide-header hide-footer centered size="md">
            <div class="d-flex flex-column align-items-center justify-content-center p-4">
                <div class="mb-3 text-warning">
                    <i class="ri-error-warning-line" style="font-size: 3.5rem;"></i>
                </div>
                <div class="message mb-4 font-weight-bold text-dark text-center" style="font-size: 1.1rem; line-height: 1.6;" v-html="$t('last_course_ni_warning')">
                </div>
                <div class="d-flex w-100 justify-content-center mt-2">
                    <b-button variant="outline-secondary" class="font-weight-bold shadow-sm mr-3 px-4" @click="$refs.warningModal.hide()">
                        {{ $t('cancel') }}
                    </b-button>
                    <b-button variant="primary" class="font-weight-bold shadow-sm px-5" @click="confirmCourse">
                        {{ $t('confirm') }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";
import showErrors from "@/helpers/showErrors"
import getDayText from "@/helpers/getDayText";

export default {
    name: "RejectedCoursesSelection",
    props: {
        countDown: {
            type: Number,
            default: 0
        },
        isWaitingMode: {
            type: Boolean,
            default: false
        }
    },
    created() {
      this.fetchCourses();
      this.setCourses();
    },
    data() {
        return {
            formData:{},
            sections: [],
            searchQuery: '',
            selectedCourses: [],
            courseFields: [
                { key: 'course_code', label: this.$t('course_code') },
                { key: 'course_name', label: this.$t('course_name') },
                { key: 'credit', label: this.$t('credit') },
                { key: 'section', label: this.$t('section') },
                { key: this.getLocaleField('course_type_name'), label:this.$t('course_type') },
                { key: 'course_day', label: this.$t('day') + '-' + this.$t('hour') },
                { key: 'instructor_name', label: this.$t('instructor') },
                { key: 'fee_simulation', label: this.$t('fee_simulation') },
                { key: 'status', label: this.$t('status') },
                { key: 'rejection_reason', label: this.$t('rejection_reason') },
                { key: 'actions', label: '' }
            ],
            pendingSelectedCourseVal: null,
            pendingCourseToAdd: null
        }
    },
    methods: {
        setCourses(){
            this.formData = this.getWithExpiry('specialStudentData', true) || {};
            if (this.formData.courses && this.formData.courses.length > 0){
                this.selectedCourses = [...this.formData.courses];
            } else {
                this.selectedCourses = [];
            }
            if (this.isWaitingMode) {
                this.courseFields = this.courseFields.filter(field => !['rejection_reason', 'actions'].includes(field.key));
            }
        },
        async fetchCourses() {
            const specialData = this.getWithExpiry('specialStudentData', true);
            const bau_program_code = specialData ? specialData.bau_program_code : null;
            if (!bau_program_code) return;
            try {
                const response = await SpecialStudentApplicationRegisterServices.getSectionList({
                    program_code: bau_program_code
                });
                this.sections = response.data.data.map((item) => {
                    return {
                        ...item,
                        text: `${item.course_code} - ${item.course_name}`,
                        value: item.section_id,
                    };
                });
            } catch (error) {
                showErrors(error);
            }
        },
        onCourseSelected(val) {
            this.pendingSelectedCourseVal = val;
        },
        addCourse() {
            if (!this.pendingSelectedCourseVal) {
                this.$toast.error(this.$t('please_select_a_course'));
                return;
            }
            const course = this.pendingSelectedCourseVal;
            if (this.selectedCourses.find(c => c.course_code === course.course_code)) {
                this.$toast.error(this.$t('already_available'));
                return;
            }

            if (['YL','D'].includes(this.formData.target_program_level) && this.selectedCourses.length >= 3) {
                this.pendingCourseToAdd = course;
                this.$refs.warningModal.show();
            } else {
                this.selectedCourses.push({ ...course, selected: false });
                this.resetSelection();
            }
        },
        resetSelection() {
            this.pendingSelectedCourseVal = null;
            this.searchQuery = null;
        },
        confirmCourse() {
            if (this.pendingCourseToAdd) {
                this.selectedCourses.push({ ...this.pendingCourseToAdd, selected: false, type: 'NI' });
            }
            this.searchQuery = null;
            this.pendingSelectedCourseVal = null;
            this.pendingCourseToAdd = null;
            this.$refs.warningModal.hide();
        },
        removeCourse(data) {
             if (data.status === "rejected") {
                 this.rejectedDeleteCourse(data.id)
                 return
             }
            this.selectedCourses = this.selectedCourses.filter(c => c.course_code !== data.course_code);
        },
        async rejectedDeleteCourse(id) {
            this.$swal
                .fire({
                    text: this.$t('are_you_sure_to_delete'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        const data ={
                            national_id:this.formData.national_id,
                            pin:this.formData.pin,
                            passport_number:this.formData.passport_number??null,
                        }
                        SpecialStudentApplicationRegisterServices.deleteRejectedCourse(id,data).then(response => {
                            this.selectedCourses = this.selectedCourses.filter(c => c.id !== id);
                            let resData = response.data.data;
                            if (resData && resData.courses) {
                                resData.courses = resData.courses.filter(c => c.id !== id);
                            }
                            this.setWithExpiry('specialStudentData', resData, this.countDown, true);
                            this.setCourses();
                            this.$toast.success(this.$t('proccess_success'));
                        }).catch(err=>{
                            this.showErrors(err,null)
                        });
                    }
                })
        },
        async submitAndNext() {
            if (this.selectedCourses.length === 0) {
                this.$toast.error(this.$t('please_select_a_course'));
                return;
            }
            const data = {
                national_id: this.formData.national_id,
                passport_number: this.formData.passport_number,
                pin: this.formData.pin,
                sections: this.selectedCourses
            }
            try {
                const res = await SpecialStudentApplicationRegisterServices.saveCourseRegistration(data);
                this.setWithExpiry('specialStudentData', res.data.data, this.countDown, true);
                this.$toast.success(this.$t("proccess_success"));
                this.$emit('success');
            } catch (err) {
                this.showErrors(err, null)
            }
        },
        dayCalculate(days) {
            let day = ''
            if (days && days.length === 0) return
            days.forEach((item) => {
                day += getDayText(item.day) + ': ' + item.start_time + '-' + item.end_time + '<br>'
            })
            return day
        }
    }
}
</script>
